import { useEffect, useState } from 'react'
import {
  fetchAutomationStatusAndAccess,
  type FetchAutomationStatusOptions,
} from '../../api/crm'

/** Permissions block returned by automation status API (Salesforce Canvas). */
export type SalesforceAutomationPermissions = {
  canCreate: boolean
  canDelete: boolean
  canUpdate: boolean
  canQuery: boolean
}

export type SalesforceRelayMetadata = {
  state: string
  eventChannel: string
  destinationResourceName: string
  label: string
}

export type SalesforceRelayStatus = {
  Id: string
  FullName: string
  Metadata: SalesforceRelayMetadata
}

export type SalesforceAutomationStatusResponse = {
  permissions: SalesforceAutomationPermissions
  relayStatus: SalesforceRelayStatus | null
}

/**
 * POST /crms/:id/salesforce/event-bridge/status response shape.
 * `permissions` is present when `x-labra-auth-provider: salesforce` (Canvas signed_request).
 * Configuration (`relayStatus`) may be returned for both `labra` and `salesforce` providers.
 */
export type SalesforceAutomationStatusApiResponse = {
  permissions?: SalesforceAutomationPermissions
  relayStatus?: SalesforceRelayStatus | null
}

/** Admin: all four true. Non-admin: canQuery true and create/update/delete false. */
export const isSalesforceAutomationsAdminFromPermissions = (
  p: SalesforceAutomationPermissions
): boolean => p.canCreate && p.canDelete && p.canUpdate && p.canQuery

/** True when relay metadata reports operational state (event-bridge enable polling). */
export const isSalesforceRelayMetadataStateRun = (
  relayStatus: SalesforceRelayStatus | null | undefined
): boolean => {
  if (relayStatus == null) {
    return false
  }
  const state = relayStatus.Metadata?.state ?? ''
  return state.trim().toUpperCase() === 'RUN'
}

/**
 * True when relay metadata reports RUN (automation fully enabled).
 * A non-null relay with another state is treated as not enabled until RUN.
 */
export const isSalesforceAutomationEnabledFromRelayStatus = (
  relayStatus: SalesforceRelayStatus | null | undefined
): boolean => isSalesforceRelayMetadataStateRun(relayStatus)

export type ParsedSalesforceEventBridgeStatus = {
  permissions: SalesforceAutomationPermissions | null
  relayStatus: SalesforceRelayStatus | null
  isRelayEnabled: boolean
}

/**
 * Fetches POST …/salesforce/event-bridge/status and normalizes the payload.
 */
export async function fetchSalesforceEventBridgeStatusParsed(
  crmId: string,
  options: FetchAutomationStatusOptions
): Promise<ParsedSalesforceEventBridgeStatus> {
  const { data } = await fetchAutomationStatusAndAccess(crmId, options)
  const status = data as SalesforceAutomationStatusApiResponse
  const relayStatus = status.relayStatus ?? null
  return {
    permissions: status.permissions ?? null,
    relayStatus,
    isRelayEnabled: isSalesforceAutomationEnabledFromRelayStatus(relayStatus),
  }
}

const EVENT_BRIDGE_STATUS_POLL_MS = 10_000

export type UseSalesforceEventBridgeStatusArgs =
  | { crmId: string; fetchEnabled: boolean; authMode: 'labra' }
  | {
      crmId: string
      fetchEnabled: boolean
      authMode: 'salesforce'
      signedRequest: string
    }

export type UseSalesforceEventBridgeStatusResult = {
  isLoading: boolean
  isRelayEnabled: boolean | null
  permissions: SalesforceAutomationPermissions | null
  relayStatus: SalesforceRelayStatus | null
}

/**
 * Loads Salesforce Event Bridge automation status for the mapping manager / settings flows.
 * For Lab web use `authMode: 'labra'`; for Canvas use `authMode: 'salesforce'` with `signedRequest`.
 */
export function useSalesforceEventBridgeStatus(
  args: UseSalesforceEventBridgeStatusArgs
): UseSalesforceEventBridgeStatusResult {
  const { crmId, fetchEnabled, authMode } = args
  const signedRequest =
    args.authMode === 'salesforce' ? args.signedRequest : undefined

  const [isLoading, setIsLoading] = useState(false)
  const [isRelayEnabled, setIsRelayEnabled] = useState<boolean | null>(null)
  const [permissions, setPermissions] =
    useState<SalesforceAutomationPermissions | null>(null)
  const [relayStatus, setRelayStatus] = useState<SalesforceRelayStatus | null>(
    null
  )

  useEffect(() => {
    if (!fetchEnabled || !crmId) {
      setIsLoading(false)
      setIsRelayEnabled(null)
      setPermissions(null)
      setRelayStatus(null)
      return
    }

    if (
      authMode === 'salesforce' &&
      (!signedRequest || signedRequest.length === 0)
    ) {
      setIsLoading(false)
      setIsRelayEnabled(null)
      setPermissions(null)
      setRelayStatus(null)
      return
    }

    let cancelled = false
    let pollTimer: ReturnType<typeof setTimeout> | null = null

    const clearPoll = () => {
      if (pollTimer != null) {
        clearTimeout(pollTimer)
        pollTimer = null
      }
    }

    const options =
      authMode === 'labra'
        ? { labraAuthProvider: 'labra' as const }
        : {
            labraAuthProvider: 'salesforce' as const,
            signedRequest: signedRequest!,
          }

    const schedulePollIfAwaitingRun = (
      relayStatus: SalesforceRelayStatus | null,
      isRun: boolean
    ) => {
      clearPoll()
      const awaitingRun = relayStatus != null && !isRun
      if (!awaitingRun) {
        return
      }
      pollTimer = setTimeout(() => {
        pollTimer = null
        void runFetch(false)
      }, EVENT_BRIDGE_STATUS_POLL_MS)
    }

    const runFetch = async (isInitial: boolean) => {
      if (cancelled) {
        return
      }
      if (isInitial) {
        setIsLoading(true)
        setIsRelayEnabled(null)
        setRelayStatus(null)
        setPermissions(null)
      }
      try {
        const parsed = await fetchSalesforceEventBridgeStatusParsed(
          crmId,
          options
        )
        if (cancelled) {
          return
        }
        const isRun = parsed.isRelayEnabled
        setIsRelayEnabled(isRun)
        setRelayStatus(parsed.relayStatus)
        setPermissions(authMode === 'labra' ? null : parsed.permissions)
        schedulePollIfAwaitingRun(parsed.relayStatus, isRun)
      } catch {
        if (cancelled) {
          return
        }
        if (isInitial) {
          setIsRelayEnabled(false)
          setPermissions(null)
          setRelayStatus(null)
        } else {
          clearPoll()
          pollTimer = setTimeout(() => {
            pollTimer = null
            void runFetch(false)
          }, EVENT_BRIDGE_STATUS_POLL_MS)
        }
      } finally {
        if (!cancelled && isInitial) {
          setIsLoading(false)
        }
      }
    }

    void runFetch(true)

    return () => {
      cancelled = true
      clearPoll()
    }
  }, [crmId, fetchEnabled, authMode, signedRequest])

  return { isLoading, isRelayEnabled, permissions, relayStatus }
}
